import React, { useState, useContext, useEffect, useMemo, useCallback } from 'react';
import { ControlProps, RankedTester, rankWith, scopeEndsWith } from '@jsonforms/core';
import { withJsonFormsControlProps } from '@jsonforms/react';
import {
  Card,
  CardContent,
  CardHeader,
  TextField,
  Typography,
  Divider,
  Box,
  Tabs,
  Tab,
} from '@mui/material';
import { WarrantyField } from './WarrantyField';
import { NodeSettingsContext } from '../../../providers/NodeSettingsProvider';

/**
 * Custom renderer for UserSettings.
 * Dynamically displays fields based on the schema.
 * Handles the 3 anyOf variants: BESS, EV, Skipper
 */
const UserSettingsRendererComponent: React.FC<ControlProps> = (props) => {
  const { data, handleChange, path, schema, enabled } = props;
  const { clientType } = useContext(NodeSettingsContext);

  // Extract all anyOf variants (memoized to avoid recalculation)
  const variants = useMemo(() => schema?.anyOf || [], [schema]);
  
  // Memoize the variant detection logic
  const detectActiveVariant = useMemo((): number => {
    if (!clientType) return 0;
    
    const normalizedClientType = clientType.toLowerCase();
    
    const variantIndex = variants.findIndex((variant: any) => {
      const title = variant.title?.toLowerCase() || '';
      return title.includes(normalizedClientType);
    });
    
    return variantIndex >= 0 ? variantIndex : 0;
  }, [clientType, variants]);

  const [selectedVariant, setSelectedVariant] = useState<number>(detectActiveVariant);

  // Update selected variant when detectActiveVariant changes
  useEffect(() => {
    setSelectedVariant(detectActiveVariant);
  }, [detectActiveVariant]);

  // Memoize current variant schema and properties
  const currentVariantSchema = useMemo(() => 
    variants[selectedVariant], 
    [variants, selectedVariant]
  );
  
  const properties = useMemo(() => 
    currentVariantSchema?.properties || {}, 
    [currentVariantSchema]
  );
  
  const genericSettings = useMemo(() => 
    data?.genericsettings || {}, 
    [data]
  );

  const genericSettingsSchema = useMemo(() => 
    properties.genericsettings?.properties || {},
    [properties]
  );

  // Get specific properties (exclude genericsettings)
  const specificProperties = useMemo(() => {
    const props = { ...properties };
    delete props.genericsettings;
    return props;
  }, [properties]);

  const hasGenericSettings = useMemo(() => 
    Object.keys(genericSettingsSchema).length > 0,
    [genericSettingsSchema]
  );
  
  const hasSpecificSettings = useMemo(() => 
    Object.keys(specificProperties).length > 0,
    [specificProperties]
  );

  // Helper to build the full path 
  const buildPath = useCallback((field: string) => `${path}.${field}`, [path]);

  // Helper to handle simple field changes 
  const handleFieldChange = useCallback((field: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    handleChange(buildPath(field), event.target.value || null);
  }, [buildPath, handleChange]);

  // Helper to handle number field changes (parse string to number)
  const handleNumberFieldChange = useCallback((field: string) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const value = event.target.value;
    // If empty string, set to null; otherwise parse to number
    const numValue = value === '' ? null : parseFloat(value);
    handleChange(buildPath(field), numValue);
  }, [buildPath, handleChange]);

  // Helper to handle warranty field changes with proper structure
  const handleWarrantyChange = useCallback((warrantyName: string, warrantySchema: any) => (newValue: any) => {
    // Extract unit from schema
    const unit = warrantySchema?.properties?.unit?.const || '';
    
    // Ensure the structure is complete even if newValue is partial
    const completeValue = newValue ? {
      value: newValue.value ?? null,
      enable: newValue.enable ?? true,
      unit: unit
    } : null;
    
    handleChange(`${path}.genericsettings.${warrantyName}`, completeValue);
  }, [path, handleChange]);

  // Handler for tab change 
  const handleTabChange = useCallback((event: React.SyntheticEvent, newValue: number) => {
    setSelectedVariant(newValue);
  }, []);

  // Render a generic field based on its schema 
  const renderField = useCallback((fieldName: string, fieldSchema: any) => {
    const fieldData = data?.[fieldName];
    const { title, description, type, format } = fieldSchema;

    // Determine the actual type (handle arrays like ["string", "null"])
    const fieldType = Array.isArray(type) ? type.find(t => t !== 'null') : type;

    // Date field
    if (format === 'date') {
      return (
        <TextField
          key={fieldName}
          label={title || fieldName}
          type={"date"}
          value={fieldData || ''}
          onChange={handleFieldChange(fieldName)}
          disabled={!enabled}
          fullWidth
          InputLabelProps={{ shrink: true }}
          helperText={description}
        />
      );
    }

    // Number field
    if (fieldType === 'number') {
      return (
        <TextField
          key={fieldName}
          label={title || fieldName}
          type={"number"}
          value={fieldData ?? ''}
          onChange={handleNumberFieldChange(fieldName)}
          disabled={!enabled}
          fullWidth
          inputProps={{ step: 0.000001 }}
          helperText={description}
        />
      );
    }

    // String field (default)
    if (fieldType === 'string') {
      return (
        <TextField
          key={fieldName}
          label={title || fieldName}
          value={fieldData || ''}
          onChange={handleFieldChange(fieldName)}
          disabled={!enabled}
          fullWidth
          placeholder={fieldSchema.pattern ? 'e.g., +00:00' : undefined}
          helperText={description}
        />
      );
    }

    return null;
  }, [data, enabled, handleFieldChange, handleNumberFieldChange]);

  // Early return if no variants
  if (variants.length === 0) {
    return <Typography color={"error"}>No user settings schema found</Typography>;
  }

  return (
    <Card elevation={2}>
      <CardHeader 
        title={currentVariantSchema?.title || 'Unknown Type'}
      />
      
      {/* Only show tabs if there are multiple variants AND no clientType is specified */}
      {!clientType && variants.length > 1 && (
        <Tabs
          value={selectedVariant}
          onChange={handleTabChange}
          variant={"fullWidth"}
          sx={{ borderBottom: 1, borderColor: 'divider' }}
        >
          {variants.map((variant: any, index: number) => (
            <Tab 
              key={index} 
              label={variant.title || `Variant ${index + 1}`}
              disabled={!enabled}
            />
          ))}
        </Tabs>
      )}
      
      <CardContent>
        {/* Specific Settings - Dynamic based on schema */}
        {hasSpecificSettings && (
          <>
            <Typography variant={"h6"} gutterBottom>
              {currentVariantSchema?.title?.split(' ')[0] || 'Specific'} Configuration
            </Typography>
            <Typography variant={"body2"} color={"text.secondary"} paragraph>
              Specific configuration parameters for this type
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 2 }}>
              {Object.entries(specificProperties).map(([fieldName, fieldSchema]: [string, any]) => 
                renderField(fieldName, fieldSchema)
              )}
            </Box>
          </>
        )}

        {/* Generic Settings - Warranties */}
        {hasGenericSettings && (
          <>
            <Typography variant={"h6"} gutterBottom>
              Warranty Thresholds
            </Typography>
            <Typography variant={"body2"} color={"text.secondary"} paragraph>
              Configure warranty conditions and thresholds for the battery system
            </Typography>

            <Box sx={{ mt: 2, mb: 3 }}>
              {Object.entries(genericSettingsSchema).map(([warrantyName, warrantySchema]: [string, any]) => {
                const unit = warrantySchema?.properties?.unit?.const || '';
                
                return (
                  <WarrantyField
                    key={warrantyName}
                    label={warrantySchema.title || warrantyName}
                    description={warrantySchema.description}
                    value={genericSettings[warrantyName] || null}
                    onChange={handleWarrantyChange(warrantyName, warrantySchema)}
                    disabled={!enabled}
                    unit={unit}
                  />
                );
              })}
            </Box>

            {hasSpecificSettings && <Divider sx={{ my: 3 }} />}
          </>
        )}
        
        {/* Message if both sections are empty */}
        {!hasGenericSettings && !hasSpecificSettings && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Typography variant={"body1"} color={"text.secondary"}>
              No specific settings available for this type
            </Typography>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export const UserSettingsRenderer = withJsonFormsControlProps(UserSettingsRendererComponent);

/**
 * Tester to determine if this renderer should be used.
 * Matches only for the scope #/properties/usersettings
 */
export const userSettingsRendererTester: RankedTester = rankWith(
  10, // High priority to override default renderers
  scopeEndsWith('usersettings')
);
